import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { WhatsAppConnectionResponse, WhatsAppSelectedPhoneNumber } from "@multi-chat/shared";
import { Badge } from "../../../components/ui/badge/index.js";
import { Button } from "../../../components/ui/button/index.js";
import {
  ConnectedChannelActions,
  ConnectedChannelCard,
  ConnectedChannelDetailList,
  ConnectedChannelDetailRow,
} from "../../../components/channel/ConnectedChannelLayout.js";
import { MixedLocaleValue, TechnicalValue } from "../TechnicalValue.js";
import {
  WhatsAppPhoneProfileAvatar,
  WhatsAppPhoneProfilePhotoDialog,
} from "./WhatsAppPhoneProfilePhotoDialog.js";

function phoneStatusVariant(
  status: WhatsAppSelectedPhoneNumber["status"],
): "success" | "warning" | "error" | "default" {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "PENDING":
      return "warning";
    case "DISABLED":
    case "RESTRICTED":
      return "error";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function PhoneStatusBadge({
  status,
}: {
  status: WhatsAppSelectedPhoneNumber["status"];
}): React.JSX.Element {
  const { t } = useTranslation();
  const key = `whatsapp.connected.phoneStatus.${status}`;
  const label = t(key);
  return (
    <Badge variant={phoneStatusVariant(status)}>
      {label === key ? status.toLowerCase() : label}
    </Badge>
  );
}

export function ConnectedDetails({
  organizationId,
  connection,
  canSwitchWorkspacePhone,
  canAddPhoneNumber,
  canUpdateBusinessProfile,
  onSwitchWorkspacePhone,
  onAddPhoneNumber,
  onProfilePictureUpdated,
  onRetryProfilePictureRefresh,
  switchBusy,
}: {
  organizationId?: number;
  connection: WhatsAppConnectionResponse;
  canSwitchWorkspacePhone?: boolean;
  canAddPhoneNumber?: boolean;
  canUpdateBusinessProfile?: boolean;
  onSwitchWorkspacePhone?: () => void;
  onAddPhoneNumber?: () => void;
  onProfilePictureUpdated?: (input: {
    externalPhoneNumberId: string;
    profilePictureUrl: string | null;
  }) => void | Promise<void>;
  onRetryProfilePictureRefresh?: () => void | Promise<void>;
  switchBusy?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [photoPhone, setPhotoPhone] = useState<WhatsAppSelectedPhoneNumber | null>(null);
  const [pictureRefresh, setPictureRefresh] = useState<{
    externalPhoneNumberId: string;
    status: "loading" | "error";
  } | null>(null);
  const currentPhone = connection.workspacePhone;
  const canUpdatePhoto = Boolean(
    organizationId != null && canUpdateBusinessProfile,
  );
  const avatarRefreshing =
    currentPhone != null &&
    pictureRefresh?.externalPhoneNumberId === currentPhone.externalPhoneNumberId &&
    pictureRefresh.status === "loading";
  const avatarRefreshError =
    currentPhone != null &&
    pictureRefresh?.externalPhoneNumberId === currentPhone.externalPhoneNumberId &&
    pictureRefresh.status === "error";

  async function handleProfilePictureUpdated(input: {
    externalPhoneNumberId: string;
    profilePictureUrl: string | null;
  }) {
    setPictureRefresh({
      externalPhoneNumberId: input.externalPhoneNumberId,
      status: "loading",
    });
    try {
      await onProfilePictureUpdated?.(input);
      setPictureRefresh((current) =>
        current?.externalPhoneNumberId === input.externalPhoneNumberId ? null : current,
      );
    } catch {
      setPictureRefresh({
        externalPhoneNumberId: input.externalPhoneNumberId,
        status: "error",
      });
    }
  }

  async function handleRetryProfileRefresh() {
    if (!currentPhone) return;
    const phoneId = currentPhone.externalPhoneNumberId;
    setPictureRefresh({ externalPhoneNumberId: phoneId, status: "loading" });
    try {
      await onRetryProfilePictureRefresh?.();
      setPictureRefresh((current) =>
        current?.externalPhoneNumberId === phoneId ? null : current,
      );
    } catch {
      setPictureRefresh({ externalPhoneNumberId: phoneId, status: "error" });
    }
  }

  return (
    <>
      <ConnectedChannelCard
        title={t("whatsapp.connected.accountSection")}
        testId="whatsapp-account-card"
      >
        <ConnectedChannelDetailList>
          <ConnectedChannelDetailRow label={t("whatsapp.connected.accountName")}>
            <MixedLocaleValue className="wrap-anywhere">
              {connection.accountName ?? t("whatsapp.dash")}
            </MixedLocaleValue>
          </ConnectedChannelDetailRow>
          <ConnectedChannelDetailRow label={t("whatsapp.connected.wabaId")}>
            <TechnicalValue className="font-mono text-[0.75rem] text-muted wrap-anywhere">
              {connection.wabaId ?? t("whatsapp.dash")}
            </TechnicalValue>
          </ConnectedChannelDetailRow>
          {connection.metaBusinessId ? (
            <ConnectedChannelDetailRow label={t("whatsapp.connected.metaBusinessId")}>
              <TechnicalValue className="font-mono text-[0.75rem] text-muted wrap-anywhere">
                {connection.metaBusinessId}
              </TechnicalValue>
            </ConnectedChannelDetailRow>
          ) : null}
        </ConnectedChannelDetailList>
      </ConnectedChannelCard>

      <ConnectedChannelCard
        title={t("whatsapp.connected.workspacePhone")}
        testId="whatsapp-workspace-phone-card"
      >
        {currentPhone == null ? (
          <p className="m-0 text-start text-[0.875rem] text-muted">
            {t("whatsapp.connected.noPhones")}
          </p>
        ) : (
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3" data-workspace-phone-identity="">
              <WhatsAppPhoneProfileAvatar
                phone={currentPhone}
                canUpdate={canUpdatePhoto}
                refreshing={avatarRefreshing}
                onOpen={() => setPhotoPhone(currentPhone)}
              />
              <div className="grid min-w-0 justify-items-start gap-0.5 text-start">
                {currentPhone.displayName ? (
                  <MixedLocaleValue className="text-[0.9rem] font-medium text-foreground wrap-anywhere">
                    {currentPhone.displayName}
                  </MixedLocaleValue>
                ) : null}
                <TechnicalValue className="font-medium tabular-nums wrap-anywhere text-foreground">
                  {currentPhone.phoneNumber}
                </TechnicalValue>
              </div>
            </div>
            <div className="shrink-0" data-workspace-phone-status="">
              <PhoneStatusBadge status={currentPhone.status} />
            </div>
          </div>
        )}
        {avatarRefreshError ? (
          <p
            className="m-0 flex flex-wrap items-center gap-2 text-start text-[0.8rem] text-error"
            role="alert"
          >
            <span>{t("whatsapp.connected.profilePhoto.refreshFailed")}</span>
            <Button
              type="button"
              variant="link"
              className="h-auto min-h-0 px-0 py-0"
              onClick={() => void handleRetryProfileRefresh()}
            >
              {t("whatsapp.connected.profilePhoto.retryRefresh")}
            </Button>
          </p>
        ) : null}
        {canAddPhoneNumber || canSwitchWorkspacePhone ? (
          <ConnectedChannelActions>
            {canAddPhoneNumber && onAddPhoneNumber ? (
              <Button
                variant="default"
                size="sm"
                className="h-8 shrink-0"
                onClick={onAddPhoneNumber}
                disabled={switchBusy}
              >
                {t("whatsapp.connected.addPhone")}
              </Button>
            ) : null}
            {canSwitchWorkspacePhone && onSwitchWorkspacePhone ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 shrink-0"
                onClick={onSwitchWorkspacePhone}
                disabled={switchBusy}
              >
                {t("whatsapp.switchWorkspacePhone.action")}
              </Button>
            ) : null}
          </ConnectedChannelActions>
        ) : null}
      </ConnectedChannelCard>
      {organizationId != null ? (
        <WhatsAppPhoneProfilePhotoDialog
          organizationId={organizationId}
          phone={photoPhone}
          open={photoPhone != null}
          onOpenChange={(open) => {
            if (!open) setPhotoPhone(null);
          }}
          onUpdated={(input) => {
            void handleProfilePictureUpdated(input);
          }}
        />
      ) : null}
    </>
  );
}
